// Buyer / Engineer Review Queue — design mock for the Orchestrator's two role-scoped views.
// Hardcoded mock data; not wired to any backend.
// This file exports a role-aware component used by both the Buyer and Engineer pages.
//
// Routes:
//   /buyer/worklist-mock                (Buyer Review Queue)
//   /engineering/review-queue-mock      (Engineer Review Queue)
//   /engineering/inbox-mock/detail      (drill-in, role-aware via ?role=)
import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  alpha,
  useTheme,
  TextField,
  InputAdornment,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  TablePagination,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  ExpandMore as ExpandIcon,
  ChevronRight as ChevronIcon,
  OpenInNew as OpenIcon,
  Snooze as SnoozeIcon,
  PersonAdd as AssignIcon,
  FiberManualRecord as DotIcon,
  ArrowBack as BackIcon,
  Add as AddIcon,
  AutoAwesome as AgentIcon,
  CheckCircle as CheckIcon,
  Cancel as RejectIcon,
  MoreVert as MoreIcon,
  Block as DismissIcon,
  Check as CheckThinIcon,
  Close as CloseThinIcon,
  ArrowOutward as ArrowOutwardIcon,
  Factory as FactoryIcon,
  Category as CategoryIcon,
  Memory as MemoryIcon,
  Bolt as BoltIcon,
  Brush as BrushIcon,
  Flight as FlightIcon,
  Build as BuildIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// ── Types ────────────────────────────────────────────────────

type TriggerType = 'PCN' | 'EOL' | 'OBSOLESCENCE' | 'NEW_ALTERNATIVE' | 'ALLOCATION' | 'COST_DOWN';
type Severity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
type GroupBy = 'PART' | 'BOM' | 'CUSTOMER';
type ReviewState = 'DISCOVERED' | 'BUYER_REVIEWING' | 'BUYER_APPROVED' | 'BUYER_REJECTED'
  | 'ENG_REVIEWING' | 'ENG_APPROVED' | 'ENG_REJECTED' | 'CONDITIONAL';
type LifecycleStage = 'PREVIEW' | 'ACTIVE' | 'NRFND' | 'LTB' | 'EOL' | 'OBSOLETE' | 'UNKNOWN';
type PartCategory = 'IC' | 'PASSIVE' | 'MATERIAL' | 'COATING' | 'ASSEMBLY' | 'GENERIC';

interface BomLine {
  lineNo: number;
  description: string;
  qtyPerAssembly: number;
  annualDemand: number;
  severity: Severity;
  lineRevenueAtRiskUsd: number;
}

interface BomImpact {
  bomId: string;
  bomName: string;
  customer: string;
  unitsPerQtr: number;
  stockWeeks: number;
  ltbWeeks: number;                  // weeks remaining until LTB date
  revenueAtRiskUsd: number;
  bestAvailableAlt?: string;         // candidate alt MPN
  bestAltState?: ReviewState;
  lines: BomLine[];
}

interface AlternativeRow {
  crossRefId: string;
  candidatePN?: string;               // internal PN if also a local part
  candidateMpn: string;
  candidateDescription: string;
  manufacturer: string;
  lifecycle: LifecycleStage;          // candidate's own lifecycle (don't approve an EOL alt!)
  reviewState: ReviewState;
  supplierCount: number;
  priceMinUsd: number | null;
  priceMaxUsd: number | null;
  bestPriceUsd: number | null;
  leadTimeWeeks: number | null;       // best (shortest) lead time across suppliers
  fffMatchScore: number;
  source: 'SYSTEM_AUTO' | 'MI_AGENT' | 'MFR_SUGGESTED' | 'BUYER_MANUAL' | 'ENG_MANUAL';
}

interface TriggerSummaryField {
  label: string;
  value: string;
}

interface WorklistItem {
  id: string;
  triggerType: TriggerType;
  subjectType: 'PART' | 'MATERIAL' | 'BOM' | 'CATEGORY';
  // Master part details — keep to the four core fields users set on a Part:
  // PN (internal part number), MPN (manufacturer part number), Manufacturer, Description.
  masterPN: string;                     // internal/local part number (e.g., "PN-12345")
  masterMpn: string;                    // manufacturer part number (e.g., "STM32F407VGT6")
  masterDescription: string;
  masterManufacturer: string;
  masterPriceUsd: number | null;
  // Kept for filtering/derivation logic; NOT rendered in the simplified card:
  masterLifecycle: LifecycleStage;
  masterCategory: PartCategory;
  masterCategoryLabel: string;
  // Outer row metrics
  severity: Severity;
  dueLabel?: string;
  revenueAtRiskUsd?: number;       // sum across all affected BOMs
  bomsAffected: number;
  linesAffected: number;           // total BOM line items touching this part
  sourceLabel: string;
  assignedToMe?: boolean;          // is this case assigned to the current user
  // Trigger summary (key/value pairs shown in expanded area)
  triggerSummary: TriggerSummaryField[];
  // Where this part is used (BOM impact)
  whereUsed: BomImpact[];
  // Inner alternatives list
  alternatives: AlternativeRow[];
}

// ── Mock data — buyer perspective ────────────────────────────

const MOCK_ITEMS: WorklistItem[] = [
  {
    id: 'wi-001',
    triggerType: 'PCN',
    subjectType: 'PART',
    masterPN: 'PN-12345',
    masterMpn: 'STM32F407VGT6',
    masterDescription: 'MCU 32-bit ARM Cortex-M4 · LQFP100',
    masterManufacturer: 'STMicroelectronics',
    masterPriceUsd: 7.95,
    masterLifecycle: 'NRFND',
    masterCategory: 'IC',
    masterCategoryLabel: 'MCU',
    severity: 'CRITICAL',
    dueLabel: 'LTB 94d',
    revenueAtRiskUsd: 4_200_000,
    bomsAffected: 5,
    linesAffected: 12,
    sourceLabel: 'PCN-ST-2026-0142',
    assignedToMe: true,
    triggerSummary: [
      { label: 'PCN reference',   value: 'PCN-ST-2026-0142' },
      { label: 'Manufacturer',    value: 'STMicroelectronics' },
      { label: 'Change type',     value: 'EOL' },
      { label: 'Effective',       value: '2026-08-10' },
      { label: 'LTB',             value: '2026-08-12' },
      { label: 'Last ship',       value: '2027-02-10' },
      { label: 'Mfr suggested',   value: 'STM32F417VGT6' },
      { label: 'Detection',       value: 'Agent feed · conf 0.92' },
    ],
    whereUsed: [
      {
        bomId: 'b-widget-a', bomName: 'BOM-Widget-A', customer: 'Acme Corp',
        unitsPerQtr: 12000, stockWeeks: 3, ltbWeeks: 13, revenueAtRiskUsd: 1_800_000,
        bestAvailableAlt: 'AAA-127', bestAltState: 'ENG_APPROVED',
        lines: [
          { lineNo: 14, description: 'Main MCU (CPU board)',  qtyPerAssembly: 2, annualDemand: 24_000, severity: 'CRITICAL', lineRevenueAtRiskUsd: 900_000 },
          { lineNo: 27, description: 'Backup MCU (failover)', qtyPerAssembly: 1, annualDemand: 12_000, severity: 'HIGH',     lineRevenueAtRiskUsd: 600_000 },
          { lineNo: 88, description: 'Diagnostic MCU',         qtyPerAssembly: 1, annualDemand: 12_000, severity: 'MEDIUM',   lineRevenueAtRiskUsd: 300_000 },
        ],
      },
      {
        bomId: 'b-widget-b', bomName: 'BOM-Widget-B', customer: 'Acme Corp',
        unitsPerQtr: 3000, stockWeeks: 8, ltbWeeks: 13, revenueAtRiskUsd: 600_000,
        bestAvailableAlt: 'AAA-127', bestAltState: 'ENG_APPROVED',
        lines: [
          { lineNo: 9,  description: 'Main MCU',   qtyPerAssembly: 1, annualDemand: 12_000, severity: 'HIGH',   lineRevenueAtRiskUsd: 400_000 },
          { lineNo: 22, description: 'Sensor MCU', qtyPerAssembly: 1, annualDemand: 12_000, severity: 'MEDIUM', lineRevenueAtRiskUsd: 200_000 },
        ],
      },
      {
        bomId: 'b-gizmo-c', bomName: 'BOM-Gizmo-C', customer: 'Initech Inc',
        unitsPerQtr: 800, stockWeeks: 1, ltbWeeks: 13, revenueAtRiskUsd: 300_000,
        lines: [
          { lineNo: 7, description: 'Controller MCU', qtyPerAssembly: 1, annualDemand: 3_200, severity: 'CRITICAL', lineRevenueAtRiskUsd: 300_000 },
        ],
      },
      {
        bomId: 'b-sensor-d', bomName: 'BOM-Sensor-D', customer: 'Megacorp',
        unitsPerQtr: 4500, stockWeeks: 5, ltbWeeks: 13, revenueAtRiskUsd: 1_200_000,
        bestAvailableAlt: 'AAA-127', bestAltState: 'ENG_APPROVED',
        lines: [
          { lineNo: 5,  description: 'MCU x2 (redundant)', qtyPerAssembly: 2, annualDemand: 18_000, severity: 'HIGH',   lineRevenueAtRiskUsd: 750_000 },
          { lineNo: 18, description: 'Telemetry MCU',       qtyPerAssembly: 1, annualDemand: 9_000,  severity: 'MEDIUM', lineRevenueAtRiskUsd: 300_000 },
          { lineNo: 41, description: 'Edge processor',      qtyPerAssembly: 1, annualDemand: 9_000,  severity: 'MEDIUM', lineRevenueAtRiskUsd: 150_000 },
        ],
      },
      {
        bomId: 'b-iot-e', bomName: 'BOM-IoT-Hub-E', customer: 'Initech Inc',
        unitsPerQtr: 2200, stockWeeks: 2, ltbWeeks: 13, revenueAtRiskUsd: 300_000,
        bestAvailableAlt: 'AAA-127', bestAltState: 'ENG_APPROVED',
        lines: [
          { lineNo: 12, description: 'Hub MCU',   qtyPerAssembly: 1, annualDemand: 8_800, severity: 'CRITICAL', lineRevenueAtRiskUsd: 200_000 },
          { lineNo: 34, description: 'Comms MCU', qtyPerAssembly: 1, annualDemand: 8_800, severity: 'HIGH',     lineRevenueAtRiskUsd: 80_000 },
          { lineNo: 51, description: 'IO MCU',    qtyPerAssembly: 1, annualDemand: 8_800, severity: 'MEDIUM',   lineRevenueAtRiskUsd: 20_000 },
        ],
      },
    ],
    alternatives: [
      {
        crossRefId: 'xref-127', candidateMpn: 'AAA-127', candidateDescription: 'Equivalent ARM Cortex-M4 MCU',
        manufacturer: 'NXP Semiconductors', lifecycle: 'ACTIVE', reviewState: 'ENG_APPROVED',
        supplierCount: 4, priceMinUsd: 7.45, priceMaxUsd: 8.10, bestPriceUsd: 7.45, leadTimeWeeks: 2, fffMatchScore: 94, source: 'SYSTEM_AUTO',
      },
      {
        crossRefId: 'xref-124', candidateMpn: 'AAA-124', candidateDescription: 'Compatible STM32-class MCU',
        manufacturer: 'GigaDevice', lifecycle: 'ACTIVE', reviewState: 'ENG_REVIEWING',
        supplierCount: 2, priceMinUsd: 8.04, priceMaxUsd: 8.65, bestPriceUsd: 8.04, leadTimeWeeks: 4, fffMatchScore: 89, source: 'MI_AGENT',
      },
      {
        crossRefId: 'xref-417', candidateMpn: 'STM32F417VGT6', candidateDescription: 'STM32F417 superset MCU',
        manufacturer: 'STMicroelectronics', lifecycle: 'ACTIVE', reviewState: 'BUYER_REVIEWING',
        supplierCount: 3, priceMinUsd: 8.40, priceMaxUsd: 9.10, bestPriceUsd: 8.40, leadTimeWeeks: 6, fffMatchScore: 96, source: 'MFR_SUGGESTED',
      },
      {
        crossRefId: 'xref-125', candidateMpn: 'AAA-125', candidateDescription: 'Single-source low-cost MCU',
        manufacturer: 'WCH', lifecycle: 'ACTIVE', reviewState: 'DISCOVERED',
        supplierCount: 1, priceMinUsd: 7.31, priceMaxUsd: 7.31, bestPriceUsd: 7.31, leadTimeWeeks: 1, fffMatchScore: 78, source: 'BUYER_MANUAL',
      },
      {
        crossRefId: 'xref-126', candidateMpn: 'AAA-126', candidateDescription: 'Older-gen MCU',
        manufacturer: 'Renesas', lifecycle: 'EOL', reviewState: 'BUYER_REJECTED',
        supplierCount: 0, priceMinUsd: null, priceMaxUsd: null, bestPriceUsd: null, leadTimeWeeks: null, fffMatchScore: 72, source: 'MI_AGENT',
      },
    ],
  },
  {
    id: 'wi-002',
    triggerType: 'ALLOCATION',
    subjectType: 'CATEGORY',
    masterPN: 'PN-22001',
    masterMpn: 'GRM155R71H104KE14D',
    masterDescription: 'MLCC 0402 X7R · 100nF / 16V',
    masterManufacturer: 'Murata (allocation declared)',
    masterPriceUsd: 0.012,
    masterLifecycle: 'ACTIVE',
    masterCategory: 'PASSIVE',
    masterCategoryLabel: 'Capacitor',
    severity: 'HIGH',
    dueLabel: 'LT 12→32w',
    revenueAtRiskUsd: 9_100_000,
    bomsAffected: 18,
    linesAffected: 47,
    sourceLabel: 'MI signal · Murata allocation',
    triggerSummary: [
      { label: 'Event source', value: 'Market Intelligence · agent inferred' },
      { label: 'Scope',        value: 'Murata · MLCC 0402 X7R' },
      { label: 'Declared',     value: '2026-05-04' },
      { label: 'Lead-time Δ',  value: '12w → 32w' },
      { label: 'Expected end', value: 'Q4 2026 (estimate)' },
      { label: 'Detection',    value: 'MI agent · conf 0.81' },
    ],
    whereUsed: [
      {
        bomId: 'b-comm-mod', bomName: 'BOM-CommModule-X1', customer: 'NorthStar Tel',
        unitsPerQtr: 24000, stockWeeks: 6, ltbWeeks: 12, revenueAtRiskUsd: 2_400_000,
        bestAvailableAlt: 'CL05B104KA5NNNC', bestAltState: 'BUYER_APPROVED',
        lines: [
          { lineNo: 22, description: 'Decoupling array (32×)', qtyPerAssembly: 32, annualDemand: 768_000, severity: 'CRITICAL', lineRevenueAtRiskUsd: 1_500_000 },
          { lineNo: 87, description: 'Power filter (8×)',       qtyPerAssembly: 8,  annualDemand: 192_000, severity: 'HIGH',     lineRevenueAtRiskUsd: 900_000 },
        ],
      },
      {
        bomId: 'b-router-pro', bomName: 'BOM-RouterPro-V3', customer: 'Initech Inc',
        unitsPerQtr: 6000, stockWeeks: 4, ltbWeeks: 12, revenueAtRiskUsd: 1_800_000,
        bestAvailableAlt: 'CL05B104KA5NNNC', bestAltState: 'BUYER_APPROVED',
        lines: [
          { lineNo: 14, description: 'Bypass cap array (16×)', qtyPerAssembly: 16, annualDemand: 384_000, severity: 'HIGH', lineRevenueAtRiskUsd: 1_800_000 },
        ],
      },
      {
        bomId: 'b-edge-iot', bomName: 'BOM-EdgeIoT-Mini', customer: 'Megacorp',
        unitsPerQtr: 18000, stockWeeks: 9, ltbWeeks: 12, revenueAtRiskUsd: 2_100_000,
        lines: [
          { lineNo: 5, description: 'MCU decoupling',  qtyPerAssembly: 4, annualDemand: 288_000, severity: 'MEDIUM', lineRevenueAtRiskUsd: 1_200_000 },
          { lineNo: 8, description: 'Radio decoupling', qtyPerAssembly: 6, annualDemand: 432_000, severity: 'HIGH',   lineRevenueAtRiskUsd: 900_000 },
        ],
      },
    ],
    alternatives: [
      {
        crossRefId: 'xref-mlcc-1', candidateMpn: 'CL05B104KA5NNNC', candidateDescription: 'MLCC 0402 X7R 100nF',
        manufacturer: 'Samsung', lifecycle: 'ACTIVE', reviewState: 'BUYER_APPROVED',
        supplierCount: 5, priceMinUsd: 0.010, priceMaxUsd: 0.014, bestPriceUsd: 0.010, leadTimeWeeks: 4, fffMatchScore: 95, source: 'SYSTEM_AUTO',
      },
      {
        crossRefId: 'xref-mlcc-2', candidateMpn: 'C0402C104K4RACTU', candidateDescription: 'MLCC 0402 X7R 100nF',
        manufacturer: 'Kemet', lifecycle: 'ACTIVE', reviewState: 'BUYER_APPROVED',
        supplierCount: 4, priceMinUsd: 0.011, priceMaxUsd: 0.013, bestPriceUsd: 0.011, leadTimeWeeks: 6, fffMatchScore: 93, source: 'SYSTEM_AUTO',
      },
      {
        crossRefId: 'xref-mlcc-3', candidateMpn: 'CC0402KRX7R8BB104', candidateDescription: 'MLCC 0402 X7R 100nF',
        manufacturer: 'Yageo', lifecycle: 'ACTIVE', reviewState: 'BUYER_REVIEWING',
        supplierCount: 3, priceMinUsd: 0.009, priceMaxUsd: 0.012, bestPriceUsd: 0.009, leadTimeWeeks: 8, fffMatchScore: 92, source: 'MI_AGENT',
      },
      {
        crossRefId: 'xref-mlcc-4', candidateMpn: 'GRM155R71H104KE14D', candidateDescription: 'MLCC 0402 X7R 100nF — Murata alternate line',
        manufacturer: 'Murata', lifecycle: 'ACTIVE', reviewState: 'DISCOVERED',
        supplierCount: 2, priceMinUsd: 0.013, priceMaxUsd: 0.016, bestPriceUsd: 0.013, leadTimeWeeks: 32, fffMatchScore: 99, source: 'MFR_SUGGESTED',
      },
    ],
  },
  {
    id: 'wi-003',
    triggerType: 'NEW_ALTERNATIVE',
    subjectType: 'PART',
    masterPN: 'PN-12345',
    masterMpn: 'STM32F407VGT6',
    masterDescription: 'MCU 32-bit ARM Cortex-M4 · LQFP100',
    masterManufacturer: 'STMicroelectronics',
    masterPriceUsd: 7.95,
    masterLifecycle: 'ACTIVE',
    masterCategory: 'IC',
    masterCategoryLabel: 'MCU',
    severity: 'MEDIUM',
    bomsAffected: 0,
    linesAffected: 0,
    sourceLabel: 'MI agent · conf 0.78',
    triggerSummary: [
      { label: 'Discovery source', value: 'Market Intelligence agent' },
      { label: 'Confidence',       value: '0.78' },
      { label: 'Discovered',       value: '2026-05-09' },
      { label: 'Master',           value: 'STM32F407VGT6' },
      { label: 'Candidate',        value: 'NXP-MCU-LPC1768' },
      { label: 'Why',              value: 'Crawled NXP cross-ref portal listing LPC1768 as STM32F4 functional equivalent' },
    ],
    whereUsed: [],  // NEW_ALTERNATIVE — not yet adopted in any BOM
    alternatives: [
      {
        crossRefId: 'xref-lpc1768', candidateMpn: 'LPC1768FBD100', candidateDescription: 'ARM Cortex-M3 MCU LQFP100',
        manufacturer: 'NXP Semiconductors', lifecycle: 'ACTIVE', reviewState: 'DISCOVERED',
        supplierCount: 3, priceMinUsd: 6.95, priceMaxUsd: 7.85, bestPriceUsd: 6.95, leadTimeWeeks: 4, fffMatchScore: 81, source: 'MI_AGENT',
      },
    ],
  },
  {
    id: 'wi-004',
    triggerType: 'OBSOLESCENCE',
    subjectType: 'MATERIAL',
    masterPN: 'PN-44012',
    masterMpn: 'AlSi12 (EN AC-44300)',
    masterDescription: 'Die-cast aluminum alloy · superseded by AlSi10Mg',
    masterManufacturer: 'Multiple foundries',
    masterPriceUsd: 3.20, // per kg
    masterLifecycle: 'EOL',
    masterCategory: 'MATERIAL',
    masterCategoryLabel: 'Aluminum alloy',
    severity: 'MEDIUM',
    dueLabel: 'EOL 60d',
    revenueAtRiskUsd: 400_000,
    bomsAffected: 2,
    linesAffected: 4,
    sourceLabel: 'Lifecycle: ACTIVE → EOL',
    triggerSummary: [
      { label: 'Lifecycle transition', value: 'ACTIVE → EOL' },
      { label: 'Effective',            value: '2026-07-08' },
      { label: 'Standard',             value: 'EN AC-44300' },
      { label: 'Superseded by',        value: 'AlSi10Mg (EN AC-43000)' },
      { label: 'Detection',            value: 'Foundry portal crawl · conf 0.78' },
    ],
    whereUsed: [
      {
        bomId: 'b-housing-x', bomName: 'BOM-Housing-X', customer: 'Acme Corp',
        unitsPerQtr: 1500, stockWeeks: 12, ltbWeeks: 9, revenueAtRiskUsd: 240_000,
        lines: [
          { lineNo: 3, description: 'Outer housing (cast)', qtyPerAssembly: 1, annualDemand: 6_000, severity: 'HIGH',   lineRevenueAtRiskUsd: 180_000 },
          { lineNo: 8, description: 'Mounting bracket',     qtyPerAssembly: 2, annualDemand: 12_000, severity: 'MEDIUM', lineRevenueAtRiskUsd: 60_000 },
        ],
      },
      {
        bomId: 'b-bracket-y', bomName: 'BOM-Bracket-Y', customer: 'Initech Inc',
        unitsPerQtr: 600, stockWeeks: 8, ltbWeeks: 9, revenueAtRiskUsd: 160_000,
        lines: [
          { lineNo: 4, description: 'Cast bracket', qtyPerAssembly: 1, annualDemand: 2_400, severity: 'MEDIUM', lineRevenueAtRiskUsd: 120_000 },
          { lineNo: 12, description: 'Cast spacer', qtyPerAssembly: 4, annualDemand: 9_600, severity: 'LOW',    lineRevenueAtRiskUsd: 40_000 },
        ],
      },
    ],
    alternatives: [
      {
        crossRefId: 'xref-alsi10mg', candidateMpn: 'AlSi10Mg', candidateDescription: 'Die-cast aluminum · primary replacement',
        manufacturer: 'Multiple', lifecycle: 'ACTIVE', reviewState: 'BUYER_REVIEWING',
        supplierCount: 6, priceMinUsd: 3.15, priceMaxUsd: 3.65, bestPriceUsd: 3.15, leadTimeWeeks: 3, fffMatchScore: 88, source: 'MFR_SUGGESTED',
      },
      {
        crossRefId: 'xref-alsi9cu3', candidateMpn: 'AlSi9Cu3(Fe)', candidateDescription: 'Secondary alloy alternative',
        manufacturer: 'Multiple', lifecycle: 'ACTIVE', reviewState: 'DISCOVERED',
        supplierCount: 4, priceMinUsd: 2.90, priceMaxUsd: 3.40, bestPriceUsd: 2.90, leadTimeWeeks: 5, fffMatchScore: 76, source: 'MI_AGENT',
      },
    ],
  },
  {
    id: 'wi-005',
    triggerType: 'PCN',
    subjectType: 'PART',
    masterPN: 'PN-000061',
    masterMpn: 'Desothane HS / CA8000',
    masterDescription: 'Polyurethane topcoat, white',
    masterManufacturer: 'PPG Aerospace',
    masterPriceUsd: 42,
    masterLifecycle: 'NRFND',
    masterCategory: 'COATING',
    masterCategoryLabel: 'Coating',
    severity: 'MEDIUM',
    dueLabel: 'LTB 120d',
    revenueAtRiskUsd: 200_000,
    bomsAffected: 3,
    linesAffected: 6,
    sourceLabel: 'PCN-PPG-2026-0078',
    assignedToMe: true,
    triggerSummary: [
      { label: 'PCN reference', value: 'PCN-PPG-2026-0078' },
      { label: 'Manufacturer',  value: 'PPG Aerospace' },
      { label: 'Change type',   value: 'Reformulation' },
      { label: 'Effective',     value: '2026-09-08' },
      { label: 'LTB',           value: '2026-09-08' },
      { label: 'Detection',     value: 'Email forward · parsed' },
    ],
    whereUsed: [
      {
        bomId: 'b-aircraft-cabin', bomName: 'BOM-Aircraft-Cabin', customer: 'Boeing',
        unitsPerQtr: 200, stockWeeks: 14, ltbWeeks: 17, revenueAtRiskUsd: 100_000,
        lines: [
          { lineNo: 18, description: 'Cabin topcoat (interior panels)', qtyPerAssembly: 5, annualDemand: 4_000, severity: 'MEDIUM', lineRevenueAtRiskUsd: 80_000 },
          { lineNo: 24, description: 'Trim accent topcoat',              qtyPerAssembly: 2, annualDemand: 1_600, severity: 'LOW',    lineRevenueAtRiskUsd: 20_000 },
        ],
      },
      {
        bomId: 'b-aircraft-trim', bomName: 'BOM-Aircraft-Trim', customer: 'Airbus',
        unitsPerQtr: 90, stockWeeks: 9, ltbWeeks: 17, revenueAtRiskUsd: 60_000,
        lines: [
          { lineNo: 12, description: 'Wing trim coat', qtyPerAssembly: 4, annualDemand: 1_440, severity: 'MEDIUM', lineRevenueAtRiskUsd: 50_000 },
          { lineNo: 35, description: 'Door trim coat', qtyPerAssembly: 2, annualDemand: 720,   severity: 'LOW',    lineRevenueAtRiskUsd: 10_000 },
        ],
      },
      {
        bomId: 'b-galley-finish', bomName: 'BOM-Galley-Finish', customer: 'Boeing',
        unitsPerQtr: 50, stockWeeks: 18, ltbWeeks: 17, revenueAtRiskUsd: 40_000,
        lines: [
          { lineNo: 7, description: 'Galley topcoat',  qtyPerAssembly: 3, annualDemand: 600, severity: 'LOW', lineRevenueAtRiskUsd: 30_000 },
          { lineNo: 22, description: 'Cabinet finish', qtyPerAssembly: 1, annualDemand: 200, severity: 'LOW', lineRevenueAtRiskUsd: 10_000 },
        ],
      },
    ],
    alternatives: [
      {
        crossRefId: 'xref-ca8000', candidateMpn: 'CA8000/BAC7067', candidateDescription: 'Desothane HS High Solids Polyurethane',
        manufacturer: 'PPG Aerospace', lifecycle: 'ACTIVE', reviewState: 'BUYER_REVIEWING',
        supplierCount: 2, priceMinUsd: 44, priceMaxUsd: 48, bestPriceUsd: 44, leadTimeWeeks: 6, fffMatchScore: 89, source: 'MFR_SUGGESTED',
      },
      {
        crossRefId: 'xref-eclg2', candidateMpn: 'ECL-G-2', candidateDescription: 'Eclipse Gloss High Solids Polyurethane',
        manufacturer: 'AkzoNobel Aerospace', lifecycle: 'ACTIVE', reviewState: 'DISCOVERED',
        supplierCount: 3, priceMinUsd: 39, priceMaxUsd: 46, bestPriceUsd: 39, leadTimeWeeks: 8, fffMatchScore: 84, source: 'MI_AGENT',
      },
    ],
  },
  {
    id: 'wi-006',
    triggerType: 'EOL',
    subjectType: 'PART',
    masterPN: 'PN-000291',
    masterMpn: 'SRB-406',
    masterDescription: 'ELT 406 MHz with NAV interface',
    masterManufacturer: 'Astronics Corporation',
    masterPriceUsd: 2400,
    masterLifecycle: 'LTB',
    masterCategory: 'ASSEMBLY',
    masterCategoryLabel: 'Avionics',
    severity: 'LOW',
    dueLabel: 'LTB 180d',
    revenueAtRiskUsd: 100_000,
    bomsAffected: 1,
    linesAffected: 1,
    sourceLabel: 'PCN-Astronics-2026-0011',
    triggerSummary: [
      { label: 'PCN reference', value: 'PCN-Astronics-2026-0011' },
      { label: 'Manufacturer',  value: 'Astronics Corporation' },
      { label: 'Change type',   value: 'EOL' },
      { label: 'Effective',     value: '2026-11-08' },
      { label: 'LTB',           value: '2026-11-08' },
    ],
    whereUsed: [
      {
        bomId: 'b-aircraft-avionics', bomName: 'BOM-Aircraft-Avionics', customer: 'Boeing',
        unitsPerQtr: 40, stockWeeks: 22, ltbWeeks: 26, revenueAtRiskUsd: 100_000,
        lines: [
          { lineNo: 31, description: 'Cockpit ELT module', qtyPerAssembly: 1, annualDemand: 160, severity: 'LOW', lineRevenueAtRiskUsd: 100_000 },
        ],
      },
    ],
    alternatives: [
      {
        crossRefId: 'xref-srb406g', candidateMpn: 'SRB-406G', candidateDescription: 'Survival type 406 MHz ELT with i-NAV',
        manufacturer: 'Astronics Corporation', lifecycle: 'ACTIVE', reviewState: 'DISCOVERED',
        supplierCount: 1, priceMinUsd: 2520, priceMaxUsd: 2520, bestPriceUsd: 2520, leadTimeWeeks: 12, fffMatchScore: 84, source: 'MFR_SUGGESTED',
      },
    ],
  },
];

// ── Visual config ────────────────────────────────────────────

// Trigger tokens: distinct hues per trigger for clear visual differentiation.
// Each ink/soft pair stays in the same color family but varies hue+lightness
// across the 6 categories so two triggers never collide.
const TRIGGER_CONFIG: Record<TriggerType, { label: string; color: string; bg: string; ink: string; soft: string }> = {
  PCN:             { label: 'PCN',             color: '#d97706', bg: '#fef3c7', ink: '#d97706', soft: '#fef3c7' },  // amber
  EOL:             { label: 'EOL',             color: '#dc2626', bg: '#fee2e2', ink: '#dc2626', soft: '#fee2e2' },  // red
  OBSOLESCENCE:    { label: 'Obsolescence',    color: '#e11d48', bg: '#ffe4e6', ink: '#e11d48', soft: '#ffe4e6' },  // rose
  NEW_ALTERNATIVE: { label: 'New alternative', color: '#0891b2', bg: '#cffafe', ink: '#0891b2', soft: '#cffafe' },  // cyan
  ALLOCATION:      { label: 'Allocation',      color: '#9333ea', bg: '#f3e8ff', ink: '#9333ea', soft: '#f3e8ff' },  // violet
  COST_DOWN:       { label: 'Cost-down',       color: '#16a34a', bg: '#dcfce7', ink: '#16a34a', soft: '#dcfce7' },  // green
};

// Stock-runway visual — fill = stockWeeks, total = ltbWeeks. Color by ratio.
// Critical < 25% · High 25–50% · OK >= 50%.
const RunwayBar: React.FC<{ stockWeeks: number; ltbWeeks: number; width?: number | string }> = ({ stockWeeks, ltbWeeks, width = 110 }) => {
  const ratio = ltbWeeks > 0 ? stockWeeks / ltbWeeks : 1;
  const fillPct = Math.max(2, Math.min(100, ratio * 100));
  const stockExceedsLtb = stockWeeks >= ltbWeeks;
  const color =
    stockExceedsLtb ? '#16a34a' :
    ratio < 0.25 ? '#dc2626' :
    ratio < 0.5  ? '#ea580c' :
    ratio < 0.75 ? '#ca8a04' :
    '#16a34a';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width }}>
      <Box sx={{
        flex: 1, height: 6, borderRadius: 999,
        bgcolor: 'rgba(15, 23, 42, 0.06)',
        overflow: 'hidden',
        position: 'relative',
      }}>
        <Box sx={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: `${fillPct}%`,
          bgcolor: color,
          borderRadius: 999,
          transition: 'width 0.4s ease',
        }} />
        {/* LTB tick marker — vertical line at end of bar */}
        <Box sx={{
          position: 'absolute', right: 0, top: -2, bottom: -2, width: 1,
          bgcolor: 'rgba(15, 23, 42, 0.35)',
        }} />
      </Box>
      <Typography variant="caption" sx={{
        fontSize: '0.68rem', fontWeight: 600, minWidth: 38, textAlign: 'right',
        color: stockExceedsLtb ? '#15803d' : ratio < 0.25 ? '#dc2626' : 'text.secondary',
        fontFeatureSettings: '"tnum"',
      }}>
        {stockWeeks}/{ltbWeeks}w
      </Typography>
    </Box>
  );
};

// Lifecycle pill — small filled badge with strong ink color.
const LIFECYCLE_CONFIG: Record<LifecycleStage, { label: string; ink: string; soft: string }> = {
  PREVIEW:  { label: 'Preview',  ink: '#0369a1', soft: '#e0f2fe' },
  ACTIVE:   { label: 'Active',   ink: '#0f766e', soft: '#ccfbf1' },
  NRFND:    { label: 'NRFND',    ink: '#b45309', soft: '#fef3c7' },
  LTB:      { label: 'LTB',      ink: '#c2410c', soft: '#ffedd5' },
  EOL:      { label: 'EOL',      ink: '#b91c1c', soft: '#fee2e2' },
  OBSOLETE: { label: 'Obsolete', ink: '#7f1d1d', soft: '#fecaca' },
  UNKNOWN:  { label: 'Unknown',  ink: '#475569', soft: '#f1f5f9' },
};

const LifecyclePill: React.FC<{ stage: LifecycleStage }> = ({ stage }) => {
  const cfg = LIFECYCLE_CONFIG[stage];
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 20,
        px: 0.875,
        borderRadius: '999px',
        fontSize: '0.65rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        bgcolor: 'transparent',
        color: cfg.ink,
        border: '1px solid',
        borderColor: alpha(cfg.ink, 0.4),
        whiteSpace: 'nowrap',
      }}
    >
      {cfg.label}
    </Box>
  );
};

const LocalTag: React.FC = () => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      height: 20,
      px: 0.875,
      borderRadius: '999px',
      fontSize: '0.65rem',
      fontWeight: 600,
      letterSpacing: '0.02em',
      bgcolor: 'transparent',
      color: '#c2410c',
      border: '1px solid',
      borderColor: alpha('#c2410c', 0.4),
      whiteSpace: 'nowrap',
    }}
  >
    Local
  </Box>
);

// Category icon — picks an appropriate icon per part category.
const CategoryIconFor: React.FC<{ category: PartCategory; sx?: object }> = ({ category, sx }) => {
  const Icon =
    category === 'IC'       ? MemoryIcon :
    category === 'PASSIVE'  ? BoltIcon :
    category === 'COATING'  ? BrushIcon :
    category === 'ASSEMBLY' ? FlightIcon :
    category === 'MATERIAL' ? BuildIcon :
    CategoryIcon;
  return <Icon sx={sx} />;
};

// Trigger gauge — circular progress ring + count + label. Clickable filter.
// Ring fill = count / maxCount across all triggers. Color matches trigger.
// Active state lifts the gauge with filled bg + stronger border.
const TriggerGauge: React.FC<{
  trigger: TriggerType | 'ALL';
  label: string;
  count: number;
  maxCount: number;
  active: boolean;
  onClick: () => void;
}> = ({ trigger, label, count, maxCount, active, onClick }) => {
  const isAll = trigger === 'ALL';
  const cfg = isAll
    ? { ink: '#475569', soft: '#f1f5f9' }   // neutral slate for All
    : { ink: TRIGGER_CONFIG[trigger as TriggerType].ink, soft: TRIGGER_CONFIG[trigger as TriggerType].soft };

  const size = 56;
  const stroke = 4;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const fillPct = maxCount > 0 ? Math.min(1, count / maxCount) : 0;
  const offset = circumference * (1 - fillPct);

  return (
    <Tooltip title={count > 0 ? `${label}: ${count} ${count === 1 ? 'case' : 'cases'}` : `${label}: no cases`} arrow placement="top">
      <Box
        onClick={onClick}
        sx={{
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.625,
          minWidth: 96,
          py: 1.25,
          px: 1.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: active ? alpha(cfg.ink, 0.4) : 'transparent',
          bgcolor: active ? cfg.soft : 'transparent',
          boxShadow: active ? `inset 0 -1px 0 ${alpha(cfg.ink, 0.12)}` : 'none',
          transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
          userSelect: 'none',
          '&:hover': {
            bgcolor: active ? cfg.soft : alpha(cfg.ink, 0.05),
            borderColor: active ? alpha(cfg.ink, 0.5) : alpha(cfg.ink, 0.2),
            transform: 'translateY(-2px)',
            boxShadow: active
              ? `0 4px 8px ${alpha(cfg.ink, 0.12)}, inset 0 -1px 0 ${alpha(cfg.ink, 0.12)}`
              : `0 2px 6px ${alpha(cfg.ink, 0.08)}`,
          },
          '&:active': {
            transform: 'translateY(-1px)',
          },
        }}
      >
        <Box sx={{ position: 'relative', width: size, height: size }}>
          <svg width={size} height={size} style={{ display: 'block', transform: 'rotate(-90deg)' }}>
            {/* Track */}
            <circle
              cx={size / 2} cy={size / 2} r={radius}
              stroke={alpha(cfg.ink, 0.1)}
              strokeWidth={stroke}
              fill="none"
            />
            {/* Fill */}
            {count > 0 && (
              <circle
                cx={size / 2} cy={size / 2} r={radius}
                stroke={cfg.ink}
                strokeWidth={stroke}
                fill="none"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
              />
            )}
          </svg>
          <Box
            sx={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.05rem', fontWeight: 700, lineHeight: 1,
              color: count > 0 ? cfg.ink : alpha(cfg.ink, 0.35),
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {count}
          </Box>
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.72rem',
            fontWeight: active ? 700 : 600,
            color: active ? cfg.ink : 'text.secondary',
            whiteSpace: 'nowrap',
            letterSpacing: '0.005em',
            textTransform: 'none',
          }}
        >
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
};

// "Mine" gauge — personal-assignment filter. Same ring pattern as TriggerGauge,
// but rendered with a person icon below the count to differentiate semantically.
const MineGauge: React.FC<{
  count: number;
  total: number;
  active: boolean;
  onClick: () => void;
}> = ({ count, total, active, onClick }) => {
  const ink = '#2563eb';   // blue — personal-assignment color
  const soft = '#dbeafe';
  const size = 56;
  const stroke = 4;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const fillPct = total > 0 ? Math.min(1, count / total) : 0;
  const offset = circumference * (1 - fillPct);

  return (
    <Tooltip title={count > 0 ? `Assigned to me: ${count} of ${total}` : 'No cases assigned to you'} arrow placement="top">
      <Box
        onClick={onClick}
        sx={{
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.625,
          minWidth: 96,
          py: 1.25,
          px: 1.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: active ? alpha(ink, 0.4) : 'transparent',
          bgcolor: active ? soft : 'transparent',
          boxShadow: active ? `inset 0 -1px 0 ${alpha(ink, 0.12)}` : 'none',
          transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
          userSelect: 'none',
          '&:hover': {
            bgcolor: active ? soft : alpha(ink, 0.05),
            borderColor: active ? alpha(ink, 0.5) : alpha(ink, 0.2),
            transform: 'translateY(-2px)',
            boxShadow: active
              ? `0 4px 8px ${alpha(ink, 0.12)}, inset 0 -1px 0 ${alpha(ink, 0.12)}`
              : `0 2px 6px ${alpha(ink, 0.08)}`,
          },
          '&:active': { transform: 'translateY(-1px)' },
        }}
      >
        <Box sx={{ position: 'relative', width: size, height: size }}>
          <svg width={size} height={size} style={{ display: 'block', transform: 'rotate(-90deg)' }}>
            <circle cx={size / 2} cy={size / 2} r={radius} stroke={alpha(ink, 0.1)} strokeWidth={stroke} fill="none" />
            {count > 0 && (
              <circle
                cx={size / 2} cy={size / 2} r={radius}
                stroke={ink} strokeWidth={stroke} fill="none"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
              />
            )}
          </svg>
          <Box sx={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.05rem', fontWeight: 700, lineHeight: 1,
            color: count > 0 ? ink : alpha(ink, 0.35),
            fontVariantNumeric: 'tabular-nums',
          }}>
            {count}
          </Box>
        </Box>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.375 }}>
          <PersonIcon sx={{ fontSize: 13, color: active ? ink : 'text.secondary' }} />
          <Typography variant="caption" sx={{
            fontSize: '0.72rem',
            fontWeight: active ? 700 : 600,
            color: active ? ink : 'text.secondary',
            whiteSpace: 'nowrap',
            letterSpacing: '0.005em',
          }}>
            Mine
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

// Reusable trigger pill — outline-only with leading dot. Lighter than filled.
const TriggerBadge: React.FC<{ trigger: TriggerType; dense?: boolean }> = ({ trigger, dense }) => {
  const cfg = TRIGGER_CONFIG[trigger];
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.625,
        height: dense ? 20 : 22,
        pl: 0.75,
        pr: 1,
        borderRadius: '999px',
        fontSize: '0.7rem',
        fontWeight: 600,
        letterSpacing: '0.005em',
        bgcolor: 'transparent',
        color: cfg.ink,
        border: '1px solid',
        borderColor: alpha(cfg.ink, 0.4),
        whiteSpace: 'nowrap',
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: cfg.ink, flexShrink: 0 }} />
      {cfg.label}
    </Box>
  );
};

const SEVERITY_COLOR: Record<Severity, string> = {
  CRITICAL: '#dc2626',
  HIGH:     '#ea580c',
  MEDIUM:   '#ca8a04',
  LOW:      '#64748b',
};

// Tokens follow a consistent {ink, soft, icon} pattern per state for a unified
// pill: ink color, hairline border at 40% of ink, semantic icon prefix
// (dot for in-progress, check for approved, cross for rejected).
type StateIconType = 'dot' | 'check' | 'cross';
const REVIEW_STATE_CONFIG: Record<
  ReviewState,
  { label: string; ink: string; soft: string; pulse?: boolean; iconType: StateIconType }
> = {
  DISCOVERED:      { label: 'Discovered',      ink: '#0369a1', soft: '#e0f2fe', pulse: true, iconType: 'dot'   },
  BUYER_REVIEWING: { label: 'Buyer review',    ink: '#b45309', soft: '#fef3c7', pulse: true, iconType: 'dot'   },
  BUYER_APPROVED:  { label: 'Buyer approved',  ink: '#0f766e', soft: '#ccfbf1',              iconType: 'check' },
  BUYER_REJECTED:  { label: 'Buyer rejected',  ink: '#991b1b', soft: '#fee2e2',              iconType: 'cross' },
  ENG_REVIEWING:   { label: 'Eng review',      ink: '#6d28d9', soft: '#ede9fe', pulse: true, iconType: 'dot'   },
  ENG_APPROVED:    { label: 'Eng approved',    ink: '#15803d', soft: '#dcfce7',              iconType: 'check' },
  ENG_REJECTED:    { label: 'Eng rejected',    ink: '#991b1b', soft: '#fee2e2',              iconType: 'cross' },
  CONDITIONAL:     { label: 'Conditional',     ink: '#a16207', soft: '#fef9c3',              iconType: 'check' },
};

// Reusable status pill — outline-only with dot prefix. Lighter feel than filled.
// Optional `pulse` adds a subtle ring animation on active states.
const StateBadge: React.FC<{ state: ReviewState }> = ({ state }) => {
  const cfg = REVIEW_STATE_CONFIG[state];
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        height: 22,
        pl: 0.75,
        pr: 1,
        borderRadius: '999px',
        fontSize: '0.7rem',
        fontWeight: 600,
        letterSpacing: '0.005em',
        bgcolor: 'transparent',
        color: cfg.ink,
        border: '1px solid',
        borderColor: alpha(cfg.ink, 0.4),
        whiteSpace: 'nowrap',
      }}
    >
      {cfg.iconType === 'dot' && (
        <Box
          sx={{
            position: 'relative',
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: cfg.ink,
            flexShrink: 0,
            ...(cfg.pulse && {
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: -3,
                borderRadius: '50%',
                border: `1px solid ${alpha(cfg.ink, 0.35)}`,
                animation: 'sb-pulse 1.8s ease-out infinite',
              },
              '@keyframes sb-pulse': {
                '0%':   { transform: 'scale(0.8)', opacity: 0.7 },
                '100%': { transform: 'scale(1.6)', opacity: 0   },
              },
            }),
          }}
        />
      )}
      {cfg.iconType === 'check' && (
        <CheckThinIcon sx={{ fontSize: 13, color: cfg.ink, flexShrink: 0 }} />
      )}
      {cfg.iconType === 'cross' && (
        <CloseThinIcon sx={{ fontSize: 13, color: cfg.ink, flexShrink: 0 }} />
      )}
      {cfg.label}
    </Box>
  );
};

// ── Helpers ──────────────────────────────────────────────────

const formatPrice = (n: number | null | undefined, master: number | null) => {
  if (n == null) return '—';
  if (n < 1) return `$${n.toFixed(3)}`;
  if (n < 100) return `$${n.toFixed(2)}`;
  return `$${n.toFixed(0)}`;
};

const pctDiff = (alt: number | null | undefined, master: number | null) => {
  if (alt == null || master == null || master === 0) return null;
  return ((alt - master) / master) * 100;
};

const ltColor = (weeks: number | null | undefined): string => {
  if (weeks == null) return '#94a3b8';
  if (weeks <= 3) return '#15803d';   // green
  if (weeks <= 12) return '#ca8a04';  // amber
  return '#dc2626';                   // red
};

const formatRevenue = (n?: number) => {
  if (n == null) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n}`;
};

const bestCandidate = (item: WorklistItem): AlternativeRow | null => {
  // best = lowest price among non-rejected alts with a price
  const eligible = item.alternatives.filter(
    (a) => a.bestPriceUsd != null && a.reviewState !== 'BUYER_REJECTED' && a.reviewState !== 'ENG_REJECTED',
  );
  if (eligible.length === 0) return null;
  return eligible.reduce((best, a) => (a.bestPriceUsd! < best.bestPriceUsd! ? a : best));
};

// ── Role config ──────────────────────────────────────────────
// Single source of truth for the differences between the buyer and engineer
// views. Same component, two role-scoped configs.
type Role = 'buyer' | 'engineer';

interface RoleConfig {
  title: string;
  crosslinkLabel: string;
  crosslinkRoute: string;
  detailRoleParam: string;
  kpis: {
    altsToReviewLabel: string;
    altsToReviewStates: ReviewState[];
    approvedByYouLabel: string;
    approvedByYouStates: ReviewState[];
  };
  // Per-alt action gating
  canApprove: (s: ReviewState) => boolean;
  canReject:  (s: ReviewState) => boolean;
  canAssign:  (s: ReviewState) => boolean;
  approveTooltip: string;
  rejectTooltip:  string;
  assignTooltip:  string;
  stateReason: (s: ReviewState) => string;
}

const ROLE_CONFIG: Record<Role, RoleConfig> = {
  buyer: {
    title:           'Buyer Review Queue',
    crosslinkLabel:  '⇆ Engineer Review Queue',
    crosslinkRoute:  '/engineering/review-queue-mock',
    detailRoleParam: 'buyer',
    kpis: {
      altsToReviewLabel:  'Alts to review',
      altsToReviewStates: ['DISCOVERED', 'BUYER_REVIEWING'],
      approvedByYouLabel: 'Approved by you',
      approvedByYouStates: ['BUYER_APPROVED', 'ENG_REVIEWING', 'ENG_APPROVED', 'CONDITIONAL'],
    },
    canApprove: (s) => s === 'DISCOVERED' || s === 'BUYER_REVIEWING',
    canReject:  (s) => s === 'DISCOVERED' || s === 'BUYER_REVIEWING',
    canAssign:  (s) => s !== 'BUYER_REJECTED' && s !== 'ENG_REJECTED' && s !== 'ENG_APPROVED',
    approveTooltip: 'Approve & send to Eng',
    rejectTooltip:  'Reject',
    assignTooltip:  'Assign reviewer',
    stateReason: (s) =>
      s === 'BUYER_APPROVED'  ? 'already approved' :
      s === 'BUYER_REJECTED'  ? 'already rejected' :
      s === 'ENG_REVIEWING'   ? 'in engineering review' :
      s === 'ENG_APPROVED'    ? 'final FFF approved' :
      s === 'ENG_REJECTED'    ? 'rejected by engineering' :
      s === 'CONDITIONAL'     ? 'conditionally approved' :
      'unavailable',
  },
  engineer: {
    title:           'Engineer Review Queue',
    crosslinkLabel:  '⇆ Buyer Review Queue',
    crosslinkRoute:  '/buyer/worklist-mock',
    detailRoleParam: 'engineer',
    kpis: {
      altsToReviewLabel:  'Alts awaiting FFF',
      altsToReviewStates: ['BUYER_APPROVED', 'ENG_REVIEWING'],
      approvedByYouLabel: 'FFF approved',
      approvedByYouStates: ['ENG_APPROVED', 'CONDITIONAL'],
    },
    canApprove: (s) => s === 'BUYER_APPROVED' || s === 'ENG_REVIEWING',
    canReject:  (s) => s === 'BUYER_APPROVED' || s === 'ENG_REVIEWING',
    canAssign:  (s) => s !== 'BUYER_REJECTED' && s !== 'ENG_REJECTED',
    approveTooltip: 'Approve FFF',
    rejectTooltip:  'Reject FFF',
    assignTooltip:  'Assign engineer',
    stateReason: (s) =>
      s === 'DISCOVERED'      ? 'awaiting buyer supply review' :
      s === 'BUYER_REVIEWING' ? 'buyer reviewing supply' :
      s === 'BUYER_REJECTED'  ? 'rejected by buyer' :
      s === 'ENG_APPROVED'    ? 'FFF already approved' :
      s === 'ENG_REJECTED'    ? 'FFF already rejected' :
      s === 'CONDITIONAL'     ? 'FFF conditionally approved' :
      'unavailable',
  },
};

// ── Component ────────────────────────────────────────────────

interface OrchestratorReviewQueueMockProps {
  role?: Role;
}

const BuyerWorklistMockPage: React.FC<OrchestratorReviewQueueMockProps> = ({ role = 'buyer' }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const roleCfg = ROLE_CONFIG[role];

  const [activeTriggers, setActiveTriggers] = useState<Set<TriggerType>>(new Set());
  const [groupBy, setGroupBy] = useState<GroupBy>('PART');
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['wi-001']));
  const [search, setSearch] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; itemId: string } | null>(null);
  const [altMenuAnchor, setAltMenuAnchor] = useState<{ el: HTMLElement; crossRefId: string; reviewState: ReviewState } | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [onlyMine, setOnlyMine] = useState(false);

  // Reusable styling for inline action icon buttons (subtle hover, square hit-target)
  const actionIconBtnSx = {
    width: 30,
    height: 30,
    borderRadius: 1.25,
    color: 'text.secondary',
    transition: 'all 0.12s',
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, 0.08),
      color: 'primary.main',
    },
  } as const;

  // Per-alt action — delicate intent color always visible, full intensity on hover.
  // Disabled state: muted gray, no hover, cursor not-allowed.
  const altActionBtnSx = (intent: 'success' | 'danger' | 'primary' | 'neutral', disabled: boolean = false) => {
    const intentColor = {
      success: '#16a34a',
      danger:  '#dc2626',
      primary: '#2563eb',
      neutral: '#475569',
    }[intent];
    if (disabled) {
      return {
        width: 26,
        height: 26,
        borderRadius: 1,
        color: alpha(theme.palette.text.primary, 0.18),
        cursor: 'not-allowed',
        '&:hover': { bgcolor: 'transparent' },
        '&.Mui-disabled': {
          color: alpha(theme.palette.text.primary, 0.18),
        },
      } as const;
    }
    return {
      width: 26,
      height: 26,
      borderRadius: 1,
      color: alpha(intentColor, 0.7),  // delicate ink — always visible
      transition: 'background-color 0.12s, color 0.12s',
      '&:hover': {
        bgcolor: alpha(intentColor, 0.1),
        color: intentColor,
      },
      '&:focus-visible': {
        outline: `2px solid ${alpha(intentColor, 0.5)}`,
        outlineOffset: 1,
      },
    } as const;
  };

  // State-aware action gating — sourced from role config
  const canApprove = roleCfg.canApprove;
  const canReject  = roleCfg.canReject;
  const canAssign  = roleCfg.canAssign;

  const toggleTrigger = (t: TriggerType) => {
    setActiveTriggers((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const counts = useMemo(() => {
    const out: Record<TriggerType, number> = {
      PCN: 0, EOL: 0, OBSOLESCENCE: 0, NEW_ALTERNATIVE: 0, ALLOCATION: 0, COST_DOWN: 0,
    };
    MOCK_ITEMS.forEach((i) => { out[i.triggerType] += 1; });
    return out;
  }, []);

  const myCount = useMemo(() => MOCK_ITEMS.filter((i) => i.assignedToMe).length, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return MOCK_ITEMS.filter((i) => {
      if (onlyMine && !i.assignedToMe) return false;
      if (activeTriggers.size > 0 && !activeTriggers.has(i.triggerType)) return false;
      if (q) {
        const partHit = `${i.masterMpn} ${i.masterDescription} ${i.masterManufacturer}`.toLowerCase().includes(q);
        const bomHit = i.whereUsed.some(
          (b) => b.bomName.toLowerCase().includes(q) || b.customer.toLowerCase().includes(q),
        );
        const altHit = i.alternatives.some(
          (a) => a.candidateMpn.toLowerCase().includes(q) || a.manufacturer.toLowerCase().includes(q),
        );
        if (!partHit && !bomHit && !altHit) return false;
      }
      return true;
    });
  }, [activeTriggers, search, onlyMine]);

  const paged = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  const totalAltsToReview = useMemo(
    () => MOCK_ITEMS.reduce((s, i) => s + i.alternatives.filter(a => roleCfg.kpis.altsToReviewStates.includes(a.reviewState)).length, 0),
    [roleCfg],
  );
  const totalApprovedByYou = useMemo(
    () => MOCK_ITEMS.reduce((s, i) => s + i.alternatives.filter(a => roleCfg.kpis.approvedByYouStates.includes(a.reviewState)).length, 0),
    [roleCfg],
  );

  const openDetail = (itemId: string) => {
    navigate(`/engineering/inbox-mock/detail?role=${roleCfg.detailRoleParam}&wi=${itemId}`);
  };

  return (
    <Box sx={{ width: '100%', pb: 4 }}>
      {/* Preview banner */}
      <Box
        sx={{
          mb: 2, px: 2, py: 1, borderRadius: 1.5,
          bgcolor: alpha(theme.palette.info.main, 0.08),
          border: '1px dashed', borderColor: alpha(theme.palette.info.main, 0.4),
          display: 'flex', alignItems: 'center', gap: 1,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.info.dark }}>
          DESIGN MOCK
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {roleCfg.title} (Orchestrator) · hardcoded data, not wired
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Button size="small" onClick={() => navigate(roleCfg.crosslinkRoute)} sx={{ textTransform: 'none' }}>
            {roleCfg.crosslinkLabel}
          </Button>
          <Button size="small" startIcon={<BackIcon />} onClick={() => navigate(role === 'engineer' ? '/engineering/queue' : '/buyer/triage')} sx={{ textTransform: 'none' }}>
            Back to {role === 'engineer' ? 'Engineering' : 'Buyer'} Triage
          </Button>
        </Box>
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {roleCfg.title}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" startIcon={<RefreshIcon />} sx={{ textTransform: 'none' }}>
            Refresh
          </Button>
        </Stack>
      </Box>

      {/* KPI strip */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mb: 3 }}>
        {[
          { label: 'Cases needing your review', value: filtered.length, color: theme.palette.primary.main },
          { label: roleCfg.kpis.altsToReviewLabel,  value: totalAltsToReview, color: '#b45309' },
          { label: roleCfg.kpis.approvedByYouLabel, value: totalApprovedByYou, color: '#0f766e' },
        ].map((k) => (
          <Paper key={k.label} elevation={0} sx={{ px: 2, py: 1.5, border: '1px solid', borderColor: theme.palette.divider, borderRadius: 1.5 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: 1.2, color: k.color }}>{k.value}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>{k.label}</Typography>
          </Paper>
        ))}
      </Box>

      {/* Combined toolbar — prominent search on left + trigger gauges on right */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 2, mb: 2,
          p: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: theme.palette.divider,
          bgcolor: 'background.paper',
        }}
      >
        {/* Search — prominent, on the left */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, minWidth: 360 }}>
          <TextField
            size="small"
            placeholder="Search part, MPN, BOM, customer, alt…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: theme.palette.text.disabled }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 360,
              '& .MuiInputBase-root': {
                bgcolor: alpha(theme.palette.text.primary, 0.02),
                borderRadius: 1.25,
              },
              '& .MuiInputBase-input': { fontSize: '0.875rem', py: 1 },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(theme.palette.text.primary, 0.08) },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(theme.palette.primary.main, 0.4) },
              '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main, borderWidth: 1 },
            }}
          />
          <Typography variant="caption" color="text.disabled" sx={{ ml: 1.5, fontVariantNumeric: 'tabular-nums' }}>
            {filtered.length} {filtered.length === 1 ? 'case' : 'cases'}
          </Typography>
        </Box>

        {/* Gauge strip — Mine + All + trigger filters, all in one row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, justifyContent: 'flex-end', overflowX: 'auto' }}>
          <MineGauge
            count={myCount}
            total={MOCK_ITEMS.length}
            active={onlyMine}
            onClick={() => setOnlyMine(!onlyMine)}
          />
          {/* Separator — divides personal filter from categorical filters */}
          <Box
            sx={{
              borderLeft: `1px dashed ${alpha(theme.palette.text.primary, 0.18)}`,
              alignSelf: 'stretch',
              my: 1.25,
              mx: 0.75,
            }}
          />
          <TriggerGauge
            trigger="ALL"
            label="All"
            count={MOCK_ITEMS.length}
            maxCount={MOCK_ITEMS.length}
            active={activeTriggers.size === 0}
            onClick={() => setActiveTriggers(new Set())}
          />
          {(Object.keys(TRIGGER_CONFIG) as TriggerType[]).map((t) => (
            <TriggerGauge
              key={t}
              trigger={t}
              label={TRIGGER_CONFIG[t].label}
              count={counts[t]}
              maxCount={Math.max(...Object.values(counts), 1)}
              active={activeTriggers.has(t)}
              onClick={() => toggleTrigger(t)}
            />
          ))}
        </Box>
      </Box>

      {/* Worklist table */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: theme.palette.divider, borderRadius: 2, overflow: 'hidden', bgcolor: '#f8fafc' }}>
        <TableContainer>
          <Table size="small" sx={{ borderCollapse: 'separate', borderSpacing: '0 8px', px: 1 }}>
            <TableHead>
              <TableRow sx={{
                '& th': {
                  fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase',
                  letterSpacing: '0.04em', color: theme.palette.text.secondary,
                  py: 1.25, border: 0, whiteSpace: 'nowrap',
                },
              }}>
                <TableCell sx={{ pl: 1, width: 36 }} />
                <TableCell sx={{ pl: 1 }}>Master Part</TableCell>
                <TableCell>Trigger</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Due</TableCell>
                <TableCell align="right">$ at risk</TableCell>
                <TableCell align="right">Where used</TableCell>
                <TableCell>Resolution</TableCell>
                <TableCell align="right">Best $ candidate</TableCell>
                <TableCell sx={{ pr: 2 }}>Source</TableCell>
                <TableCell align="right" sx={{ pr: 2 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paged.map((item) => {
                const cfg = TRIGGER_CONFIG[item.triggerType];
                const isExpanded = expanded.has(item.id);
                const sevColor = SEVERITY_COLOR[item.severity];
                const bestAlt = bestCandidate(item);
                const bestDiffPct = bestAlt && item.masterPriceUsd ? pctDiff(bestAlt.bestPriceUsd, item.masterPriceUsd) : null;
                return (
                  <React.Fragment key={item.id}>
                    {/* OUTER ROW */}
                    <TableRow
                      onClick={() => toggleExpand(item.id)}
                      sx={{
                        cursor: 'pointer', bgcolor: '#fff',
                        boxShadow: '0 1px 0 rgba(15, 23, 42, 0.04)',
                        '& td': { border: 0, py: 1.5 },
                        '&:hover td': { bgcolor: alpha(theme.palette.primary.main, 0.03) },
                        position: 'relative',
                      }}
                    >
                      <TableCell sx={{ pl: 1, width: 36, position: 'relative' }}>
                        <Box sx={{ position: 'absolute', left: 0, top: 8, bottom: 8, width: 3, borderRadius: 1, bgcolor: sevColor }} />
                        <IconButton size="small" sx={{ p: 0.25 }}>
                          {isExpanded ? <ExpandIcon sx={{ fontSize: 18 }} /> : <ChevronIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                      </TableCell>

                      {/* Master Part — PN + MPN + Manufacturer + Description */}
                      <TableCell sx={{ pl: 1, minWidth: 320 }}>
                        <Box sx={{ minWidth: 0 }}>
                          {/* Line 1: PN — primary identifier */}
                          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.25 }}>
                            {item.masterPN}
                          </Typography>
                          {/* Line 2: MPN — manufacturer's part number */}
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block', fontWeight: 600, fontSize: '0.78rem',
                              color: 'text.primary', lineHeight: 1.3,
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                            }}
                          >
                            {item.masterMpn}
                          </Typography>
                          {/* Line 3: Manufacturer */}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', lineHeight: 1.3, fontWeight: 500 }}
                          >
                            {item.masterManufacturer}
                          </Typography>
                          {/* Line 4: Description */}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: 'block', lineHeight: 1.3, mt: 0.125,
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}
                          >
                            {item.masterDescription}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <TriggerBadge trigger={item.triggerType} />
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <DotIcon sx={{ fontSize: 10, color: sevColor }} />
                          <Typography variant="caption" sx={{ fontWeight: 600, color: sevColor, textTransform: 'capitalize' }}>
                            {item.severity.toLowerCase()}
                          </Typography>
                        </Stack>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {item.dueLabel
                          ? <Typography variant="caption" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{item.dueLabel}</Typography>
                          : <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>

                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                          {formatRevenue(item.revenueAtRiskUsd)}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {item.bomsAffected} BOM{item.bomsAffected !== 1 ? 's' : ''} · {item.linesAffected} line{item.linesAffected !== 1 ? 's' : ''}
                        </Typography>
                      </TableCell>

                      {/* Resolution — alts pipeline summary */}
                      <TableCell>
                        {(() => {
                          const activeAlts = item.alternatives.filter(
                            (a) => a.reviewState !== 'BUYER_REJECTED' && a.reviewState !== 'ENG_REJECTED',
                          ).length;
                          const approved = item.alternatives.filter(
                            (a) => a.reviewState === 'ENG_APPROVED' || a.reviewState === 'CONDITIONAL',
                          ).length;
                          return (
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                              <Box component="span" sx={{ fontWeight: 600 }}>{activeAlts}</Box>
                              <Box component="span" sx={{ color: 'text.secondary' }}> alt{activeAlts !== 1 ? 's' : ''} · </Box>
                              <Box component="span" sx={{ fontWeight: 700, color: approved > 0 ? '#16a34a' : 'text.secondary' }}>
                                {approved} approved
                              </Box>
                            </Typography>
                          );
                        })()}
                      </TableCell>

                      {/* Best $ candidate — price + smaller %diff */}
                      <TableCell align="right">
                        {bestAlt && bestAlt.bestPriceUsd != null ? (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', lineHeight: 1.2 }}>
                              {formatPrice(bestAlt.bestPriceUsd, item.masterPriceUsd)}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ fontSize: '0.65rem', fontWeight: 600, display: 'block', lineHeight: 1.1 }}
                            >
                              {bestDiffPct != null && (
                                <Box component="span" sx={{ color: bestDiffPct < 0 ? '#15803d' : bestDiffPct > 5 ? '#dc2626' : '#ca8a04' }}>
                                  {bestDiffPct > 0 ? '+' : ''}{bestDiffPct.toFixed(1)}%
                                </Box>
                              )}
                              {bestAlt.leadTimeWeeks != null && (
                                <Box component="span" sx={{ ml: bestDiffPct != null ? 0.5 : 0, color: ltColor(bestAlt.leadTimeWeeks) }}>
                                  · {bestAlt.leadTimeWeeks}w
                                </Box>
                              )}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{ pr: 2, maxWidth: 180 }}>
                        <Tooltip title={item.sourceLabel} arrow placement="top">
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {item.sourceLabel}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      <TableCell align="right" sx={{ pr: 1.5 }}>
                        <Stack
                          direction="row"
                          spacing={0.25}
                          justifyContent="flex-end"
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            display: 'inline-flex',
                            p: 0.25,
                            borderRadius: 1.5,
                            bgcolor: alpha(theme.palette.text.primary, 0.025),
                            border: '1px solid',
                            borderColor: alpha(theme.palette.text.primary, 0.06),
                          }}
                        >
                          <Tooltip title="Open" arrow placement="top">
                            <IconButton size="small" sx={actionIconBtnSx} onClick={() => openDetail(item.id)}>
                              <OpenIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={item.assignedToMe ? 'Reassign…' : 'Assign…'} arrow placement="top">
                            <IconButton
                              size="small"
                              sx={{
                                ...actionIconBtnSx,
                                color: item.assignedToMe ? '#2563eb' : actionIconBtnSx.color,
                              }}
                            >
                              <AssignIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: alpha(theme.palette.text.primary, 0.08), mx: 0.25 }} />
                          <Tooltip title="More actions" arrow placement="top">
                            <IconButton
                              size="small"
                              sx={actionIconBtnSx}
                              onClick={(e) => setMenuAnchor({ el: e.currentTarget, itemId: item.id })}
                            >
                              <MoreIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>

                    {/* EXPANDED ROW */}
                    <TableRow sx={{ '& td': { border: 0, p: 0 } }}>
                      <TableCell colSpan={11} sx={{ p: 0 }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{
                            mt: -1, mb: 1, ml: 5, mr: 1, p: 2, borderRadius: 1.5,
                            bgcolor: alpha(theme.palette.primary.main, 0.025),
                            border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.12),
                          }}>
                            {/* Trigger Summary panel */}
                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              Trigger summary
                            </Typography>
                            <Box sx={{
                              mt: 1, mb: 2, p: 1.5, borderRadius: 1, bgcolor: '#fff',
                              border: '1px solid', borderColor: theme.palette.divider,
                              display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1,
                            }}>
                              {item.triggerSummary.map((f) => (
                                <Box key={f.label}>
                                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    {f.label}
                                  </Typography>
                                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 500 }}>
                                    {f.value}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>

                            {/* Where used (BOM impact) */}
                            {item.whereUsed.length > 0 && (
                              <>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                  Where used · {item.bomsAffected} BOM{item.bomsAffected !== 1 ? 's' : ''} · {item.linesAffected} lines · {formatRevenue(item.revenueAtRiskUsd)} @ risk
                                </Typography>
                                <Box sx={{ mt: 1, mb: 2, borderRadius: 1, bgcolor: '#fff', border: '1px solid', borderColor: theme.palette.divider, overflow: 'hidden' }}>
                                  {/* Header row */}
                                  <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1.4fr 1fr 1.4fr 0.55fr 0.85fr 0.85fr 1.3fr',
                                    gap: 1, py: 1, px: 1.25,
                                    bgcolor: alpha(theme.palette.text.primary, 0.02),
                                    borderBottom: '1px solid', borderColor: theme.palette.divider,
                                    fontSize: '0.65rem', fontWeight: 600, color: 'text.secondary',
                                    textTransform: 'uppercase', letterSpacing: '0.04em',
                                  }}>
                                    <Box>BOM</Box>
                                    <Box>Customer</Box>
                                    <Box>Stock runway → LTB</Box>
                                    <Box>Lines</Box>
                                    <Box>Qty / qtr</Box>
                                    <Box>$ at risk</Box>
                                    <Box>Best alt</Box>
                                  </Box>
                                  {/* BOM rows */}
                                  {item.whereUsed
                                    .slice()
                                    .sort((a, b) => b.revenueAtRiskUsd - a.revenueAtRiskUsd)
                                    .map((bom) => {
                                      const bomKey = `${item.id}-${bom.bomId}`;
                                      const isExpanded = expanded.has(bomKey);
                                      const altApproved = bom.bestAltState === 'ENG_APPROVED' || bom.bestAltState === 'CONDITIONAL';
                                      const ratio = bom.stockWeeks / bom.ltbWeeks;
                                      const isCritical = ratio < 0.25;
                                      return (
                                        <React.Fragment key={bom.bomId}>
                                          <Box
                                            onClick={(e) => { e.stopPropagation(); toggleExpand(bomKey); }}
                                            sx={{
                                              display: 'grid',
                                              gridTemplateColumns: '1.4fr 1fr 1.4fr 0.55fr 0.85fr 0.85fr 1.3fr',
                                              gap: 1, py: 1, px: 1.25, alignItems: 'center',
                                              borderTop: '1px solid', borderColor: alpha(theme.palette.divider, 0.6),
                                              cursor: 'pointer',
                                              position: 'relative',
                                              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.025) },
                                            }}
                                          >
                                            {/* severity stripe on the left */}
                                            <Box sx={{
                                              position: 'absolute', left: 0, top: 4, bottom: 4, width: 3,
                                              borderRadius: 1,
                                              bgcolor: isCritical ? '#dc2626' : ratio < 0.5 ? '#ea580c' : 'transparent',
                                            }} />
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                              {isExpanded
                                                ? <ExpandIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                                : <ChevronIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}
                                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.825rem' }}>{bom.bomName}</Typography>
                                            </Box>
                                            <Typography variant="caption" sx={{ fontWeight: 500 }}>{bom.customer}</Typography>
                                            <RunwayBar stockWeeks={bom.stockWeeks} ltbWeeks={bom.ltbWeeks} />
                                            <Typography variant="caption" sx={{ fontWeight: 600 }}>{bom.lines.length}</Typography>
                                            <Typography variant="caption">{bom.unitsPerQtr.toLocaleString()}</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.825rem' }}>{formatRevenue(bom.revenueAtRiskUsd)}</Typography>
                                            <Box>
                                              {bom.bestAvailableAlt ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                  <Box sx={{
                                                    width: 5, height: 5, borderRadius: '50%',
                                                    bgcolor: altApproved ? '#16a34a' : '#ca8a04',
                                                    flexShrink: 0,
                                                  }} />
                                                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                                    {bom.bestAvailableAlt}
                                                  </Typography>
                                                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: altApproved ? '#15803d' : '#a16207' }}>
                                                    {altApproved ? 'ready' : 'pending'}
                                                  </Typography>
                                                </Box>
                                              ) : (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#dc2626', flexShrink: 0 }} />
                                                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem', color: '#dc2626' }}>
                                                    no alt yet
                                                  </Typography>
                                                </Box>
                                              )}
                                            </Box>
                                          </Box>
                                          {/* Expanded line breakdown */}
                                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                            <Box sx={{ px: 4, py: 0.75, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                                              {bom.lines.map((line) => {
                                                const sevColor = SEVERITY_COLOR[line.severity];
                                                return (
                                                  <Box key={line.lineNo} sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '60px 2fr 0.7fr 1fr 0.8fr 0.9fr',
                                                    gap: 1, py: 0.5, alignItems: 'center',
                                                    fontSize: '0.78rem',
                                                  }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                                      Line {line.lineNo}
                                                    </Typography>
                                                    <Typography variant="caption">{line.description}</Typography>
                                                    <Typography variant="caption" color="text.secondary">qty {line.qtyPerAssembly}</Typography>
                                                    <Typography variant="caption" color="text.secondary">{line.annualDemand.toLocaleString()}/yr</Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: sevColor }} />
                                                      <Typography variant="caption" sx={{ fontWeight: 600, color: sevColor, textTransform: 'capitalize' }}>
                                                        {line.severity.toLowerCase()}
                                                      </Typography>
                                                    </Box>
                                                    <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'right', pr: 1 }}>
                                                      {formatRevenue(line.lineRevenueAtRiskUsd)}
                                                    </Typography>
                                                  </Box>
                                                );
                                              })}
                                            </Box>
                                          </Collapse>
                                        </React.Fragment>
                                      );
                                    })}
                                </Box>
                              </>
                            )}

                            {/* Alternatives inner table */}
                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              Alternatives ({item.alternatives.length})
                            </Typography>
                            <Box sx={{ mt: 1, borderRadius: 1, bgcolor: '#fff', border: '1px solid', borderColor: theme.palette.divider, overflow: 'hidden' }}>
                              {/* Header */}
                              <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: '1.55fr 0.9fr 0.5fr 0.9fr 0.8fr 0.5fr 1.25fr',
                                gap: 1, py: 1, px: 1.25,
                                bgcolor: alpha(theme.palette.text.primary, 0.02),
                                borderBottom: '1px solid', borderColor: theme.palette.divider,
                                fontSize: '0.65rem', fontWeight: 600, color: 'text.secondary',
                                textTransform: 'uppercase', letterSpacing: '0.04em',
                              }}>
                                <Box>Alternative Part</Box>
                                <Box>State</Box>
                                <Box># Suppliers</Box>
                                <Box>Price Range</Box>
                                <Box>Best Price</Box>
                                <Box>FFF Score</Box>
                                <Box sx={{ textAlign: 'right' }}>Actions</Box>
                              </Box>
                              {/* Rows */}
                              {item.alternatives.map((alt) => {
                                const altDiffPct = pctDiff(alt.bestPriceUsd, item.masterPriceUsd);
                                const isTerminalRejected = alt.reviewState === 'BUYER_REJECTED' || alt.reviewState === 'ENG_REJECTED';
                                return (
                                  <Box
                                    key={alt.crossRefId}
                                    sx={{
                                      display: 'grid',
                                      gridTemplateColumns: '1.55fr 0.9fr 0.5fr 0.9fr 0.8fr 0.5fr 1.25fr',
                                      gap: 1, alignItems: 'center', py: 1, px: 1.25,
                                      borderTop: '1px solid', borderColor: alpha(theme.palette.divider, 0.6),
                                      opacity: isTerminalRejected ? 0.55 : 1,
                                      cursor: 'pointer',
                                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) },
                                    }}
                                    onClick={() => openDetail(item.id)}
                                  >
                                    {/* Alternative Part — PN (if any) + MPN + Manufacturer + Description */}
                                    <Box sx={{ minWidth: 0 }}>
                                      {alt.candidatePN && (
                                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.825rem', lineHeight: 1.25 }}>
                                          {alt.candidatePN}
                                        </Typography>
                                      )}
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          display: 'block',
                                          fontWeight: alt.candidatePN ? 600 : 700,
                                          fontSize: alt.candidatePN ? '0.74rem' : '0.825rem',
                                          color: 'text.primary',
                                          lineHeight: 1.3,
                                          fontFamily: alt.candidatePN ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit',
                                        }}
                                      >
                                        {alt.candidateMpn}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', lineHeight: 1.3, fontWeight: 500 }}
                                      >
                                        {alt.manufacturer}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', lineHeight: 1.3, mt: 0.125, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                      >
                                        {alt.candidateDescription}
                                      </Typography>
                                    </Box>
                                    {/* State */}
                                    <Box>
                                      <StateBadge state={alt.reviewState} />
                                    </Box>
                                    {/* # Suppliers */}
                                    <Box>
                                      <Typography variant="caption" sx={{ fontWeight: 600, color: alt.supplierCount === 0 ? '#dc2626' : alt.supplierCount === 1 ? '#ca8a04' : 'text.primary' }}>
                                        {alt.supplierCount}
                                      </Typography>
                                    </Box>
                                    {/* Price Range */}
                                    <Box>
                                      {alt.priceMinUsd != null && alt.priceMaxUsd != null
                                        ? alt.priceMinUsd === alt.priceMaxUsd
                                          ? <Typography variant="caption">{formatPrice(alt.priceMinUsd, item.masterPriceUsd)} <Box component="span" sx={{ color: 'text.disabled' }}>(single)</Box></Typography>
                                          : <Typography variant="caption">{formatPrice(alt.priceMinUsd, item.masterPriceUsd)} – {formatPrice(alt.priceMaxUsd, item.masterPriceUsd)}</Typography>
                                        : <Typography variant="caption" color="text.disabled">—</Typography>}
                                    </Box>
                                    {/* Best Price + diff + LT */}
                                    <Box>
                                      {alt.bestPriceUsd != null ? (
                                        <>
                                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.2 }}>
                                            {formatPrice(alt.bestPriceUsd, item.masterPriceUsd)}
                                          </Typography>
                                          <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600, display: 'block', lineHeight: 1.1 }}>
                                            {altDiffPct != null && (
                                              <Box component="span" sx={{ color: altDiffPct < 0 ? '#15803d' : altDiffPct > 5 ? '#dc2626' : '#ca8a04' }}>
                                                {altDiffPct > 0 ? '+' : ''}{altDiffPct.toFixed(1)}%
                                              </Box>
                                            )}
                                            {alt.leadTimeWeeks != null && (
                                              <Box component="span" sx={{ ml: altDiffPct != null ? 0.5 : 0, color: ltColor(alt.leadTimeWeeks) }}>
                                                · {alt.leadTimeWeeks}w
                                              </Box>
                                            )}
                                          </Typography>
                                        </>
                                      ) : (
                                        <Typography variant="caption" color="text.disabled">—</Typography>
                                      )}
                                    </Box>
                                    {/* FFF Score */}
                                    <Box>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 700, fontSize: '0.85rem',
                                          color: alt.fffMatchScore >= 90 ? '#15803d' : alt.fffMatchScore >= 80 ? '#ca8a04' : '#dc2626',
                                        }}
                                      >
                                        {alt.fffMatchScore}
                                      </Typography>
                                    </Box>
                                    {/* Actions — always all 5 visible, gated per state */}
                                    {(() => {
                                      const approveDisabled = !canApprove(alt.reviewState);
                                      const rejectDisabled  = !canReject(alt.reviewState);
                                      const tooltipFor = (action: string, disabled: boolean, reason: string) =>
                                        disabled ? `${action} — ${reason}` : action;
                                      const stateReason = roleCfg.stateReason(alt.reviewState);
                                      return (
                                        <Stack
                                          direction="row"
                                          spacing={0}
                                          onClick={(e) => e.stopPropagation()}
                                          sx={{
                                            display: 'inline-flex',
                                            ml: 'auto',
                                            p: 0.25,
                                            borderRadius: 1.25,
                                            bgcolor: alpha(theme.palette.text.primary, 0.025),
                                            border: '1px solid',
                                            borderColor: alpha(theme.palette.text.primary, 0.06),
                                          }}
                                        >
                                          <Tooltip title={tooltipFor(roleCfg.approveTooltip, approveDisabled, stateReason)} arrow placement="top">
                                            <span>
                                              <IconButton size="small" disabled={approveDisabled} sx={altActionBtnSx('success', approveDisabled)}>
                                                <CheckThinIcon sx={{ fontSize: 16 }} />
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                          <Tooltip title={tooltipFor(roleCfg.rejectTooltip, rejectDisabled, stateReason)} arrow placement="top">
                                            <span>
                                              <IconButton size="small" disabled={rejectDisabled} sx={altActionBtnSx('danger', rejectDisabled)}>
                                                <CloseThinIcon sx={{ fontSize: 16 }} />
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                          <Tooltip title="Open compare" arrow placement="top">
                                            <IconButton size="small" sx={altActionBtnSx('neutral')} onClick={() => openDetail(item.id)}>
                                              <ArrowOutwardIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                          </Tooltip>
                                          <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: alpha(theme.palette.text.primary, 0.08), mx: 0.25 }} />
                                          <Tooltip title="More" arrow placement="top">
                                            <IconButton
                                              size="small"
                                              sx={altActionBtnSx('neutral')}
                                              onClick={(e) => setAltMenuAnchor({ el: e.currentTarget, crossRefId: alt.crossRefId, reviewState: alt.reviewState })}
                                            >
                                              <MoreIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                          </Tooltip>
                                        </Stack>
                                      );
                                    })()}
                                  </Box>
                                );
                              })}
                            </Box>

                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 20, 50]}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          sx={{
            borderTop: '1px solid',
            borderColor: theme.palette.divider,
            bgcolor: '#fff',
            '& .MuiToolbar-root': { minHeight: 44 },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.75rem' },
          }}
        />
      </Paper>

      {/* Overflow menu for outer-row "More actions" */}
      <Menu
        anchorEl={menuAnchor?.el}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 0.5,
              minWidth: 220,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: theme.palette.divider,
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
              '& .MuiMenuItem-root': {
                fontSize: '0.825rem',
                py: 0.875,
                px: 1.5,
                gap: 1,
              },
              '& .MuiListItemIcon-root': {
                minWidth: 28,
                color: 'text.secondary',
              },
            },
          },
        }}
      >
        <MenuItem onClick={() => setMenuAnchor(null)}>
          <ListItemIcon><SnoozeIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Snooze…</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => setMenuAnchor(null)}>
          <ListItemIcon><AssignIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Assign to user…</ListItemText>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={() => setMenuAnchor(null)}>
          <ListItemIcon><CheckIcon fontSize="small" sx={{ color: '#15803d' }} /></ListItemIcon>
          <ListItemText>Mark resolved</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => setMenuAnchor(null)}>
          <ListItemIcon><DismissIcon fontSize="small" sx={{ color: '#dc2626' }} /></ListItemIcon>
          <ListItemText>Dismiss…</ListItemText>
        </MenuItem>
      </Menu>

      {/* Per-alt overflow menu */}
      <Menu
        anchorEl={altMenuAnchor?.el}
        open={!!altMenuAnchor}
        onClose={() => setAltMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 0.5,
              minWidth: 220,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: theme.palette.divider,
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
              '& .MuiMenuItem-root': { fontSize: '0.825rem', py: 0.875, px: 1.5, gap: 1 },
              '& .MuiListItemIcon-root': { minWidth: 28, color: 'text.secondary' },
            },
          },
        }}
      >
        <MenuItem onClick={() => setAltMenuAnchor(null)}>
          <ListItemIcon><SnoozeIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Park alternative</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => setAltMenuAnchor(null)}>
          <ListItemIcon><AssignIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Assign reviewer…</ListItemText>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={() => setAltMenuAnchor(null)}>
          <ListItemIcon><AgentIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Re-run agent search</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => setAltMenuAnchor(null)}>
          <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Add note…</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => setAltMenuAnchor(null)}>
          <ListItemIcon><OpenIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Copy MPN</ListItemText>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        {altMenuAnchor && (altMenuAnchor.reviewState === 'BUYER_APPROVED' || altMenuAnchor.reviewState === 'ENG_REVIEWING' || altMenuAnchor.reviewState === 'ENG_APPROVED') && (
          <MenuItem onClick={() => setAltMenuAnchor(null)}>
            <ListItemIcon><DismissIcon fontSize="small" sx={{ color: '#dc2626' }} /></ListItemIcon>
            <ListItemText>Reset review (back to Discovered)</ListItemText>
          </MenuItem>
        )}
        {altMenuAnchor && (altMenuAnchor.reviewState === 'BUYER_REJECTED' || altMenuAnchor.reviewState === 'ENG_REJECTED') && (
          <MenuItem onClick={() => setAltMenuAnchor(null)}>
            <ListItemIcon><CheckIcon fontSize="small" sx={{ color: '#15803d' }} /></ListItemIcon>
            <ListItemText>Re-open this alternative</ListItemText>
          </MenuItem>
        )}
      </Menu>

    </Box>
  );
};

export default BuyerWorklistMockPage;
